import { Alert, AlertIcon, Flex, Box, Text, Link, Progress } from '@chakra-ui/react';
import { Fragment } from 'react';
import { _js } from '@ifixit/localize';
import { OrderType, TrackingType } from './orders_types';

export function LatestOrder({ order }: { order: OrderType }) {
   const shipped = order.currentStep === 4;
   return (
      <Flex direction="column">
         <Flex fontWeight="bold" justify="space-between" align="baseline">
            <Text fontSize="18px" maxW={{ base: '50%', md: 'unset' }}>
               {_js('Current Status: ')} {order.status}
            </Text>
            <Flex align="baseline" fontSize="14px">
               <Text>{_js('Order Number:')}</Text>
               <Link ml="4px" color="brand.600" href={order.invoiceLink} className="orderNumber">
                  {order.order_name}
               </Link>
            </Flex>
         </Flex>
         <ProgressBar progress={order.widthPercent} complete={shipped} />
         <StatusLabels currentStep={order.currentStep} />
         {shipped && <ShippedMessage tracking={order.shipmentTracking} />}
      </Flex>
   );
}

const ProgressBar = ({ progress, complete }: { progress: number; complete: boolean }) => (
   <Progress
      h="30px"
      mt="24px"
      w={{ base: '95vw', lg: '100%' }}
      borderRadius="4px"
      isAnimated={!complete}
      hasStripe={!complete}
      colorScheme={complete ? 'green' : 'brand'}
      bgImage={'linear-gradient(to top, #f4f4f4, #dedede)'}
      value={progress}
   />
);

const StatusLabels = ({ currentStep }: { currentStep: number }) => {
   const labels = [
      { text: _js('Order Placed') },
      { text: _js('Processing Order') },
      { text: _js('Preparing For Shipment') },
      { text: _js('Shipped'), isGreen: true },
   ];

   return (
      <Flex justify="space-between" direction={{ base: 'column', md: 'row' }}>
         {labels.map((label, i) => (
            <Label
               key={label.text}
               text={label.text}
               status={getStatus(currentStep, i)}
               isGreen={label.isGreen}
            />
         ))}
      </Flex>
   );
};

type StatusType = 'completed' | 'current' | 'inactive';

function getStatus(currentStep: number, index = 0): StatusType {
   const step = index + 1;
   if (currentStep > step) {
      return 'completed';
   } else if (currentStep === step) {
      return 'current';
   } else {
      return 'inactive';
   }
}

const ShippedMessage = ({ tracking }: { tracking?: TrackingType }) => (
   <Alert status="success" fontSize="18px" mt="30px">
      <AlertIcon />
      {_js('Your order has shipped!')}
      {tracking && (
         <Fragment>
            <Text>{_js('Tracking number: ')}</Text>
            <Link href={tracking.link}>{tracking.number}</Link>
         </Fragment>
      )}
   </Alert>
);

const Label = ({
   text,
   status,
   isGreen,
}: {
   text: string;
   status: StatusType;
   isGreen?: boolean;
}) => {
   const inactiveLabel = {
      color: 'gray.500',
   };
   const activeLabel = {
      color: 'black',
      fontWeight: 'bold',
   };
   const textStyle = status === 'inactive' ? inactiveLabel : activeLabel;
   return (
      <Flex align="center">
         <LabelIcon status={status} isGreen={isGreen} />
         <Text ml="8px" {...textStyle}>
            {text}
         </Text>
      </Flex>
   );
};

const baseIconStyle = {
   width: '16px',
   height: '16px',
   borderRadius: '16px',
   backgroundColor: 'transparent',
   border: 'solid 2px #d3d4d4',
};

const LabelIcon = ({ status, isGreen }: { status: StatusType; isGreen?: boolean }) => {
   if (status === 'inactive') {
      return <Box {...baseIconStyle} />;
   }
   return <ActiveIcon status={status} isGreen={isGreen} />;
};

const ActiveIcon = ({ status, isGreen }: { status: StatusType; isGreen?: boolean }) => {
   const statusBorder = isGreen ? '#62cc28' : 'brand.500';
   let statusBackground;
   if (status !== 'current') {
      statusBackground = 'linear-gradient(to bottom, #0079dc, #0065b8)';
   }
   if (isGreen) {
      statusBackground = 'linear-gradient(to bottom, #62cc28, #4b9f1e)';
   }

   return <Box {...baseIconStyle} borderColor={statusBorder} bgImage={statusBackground} />;
};
